import { useState, useEffect } from "react";
import { 
  Scale, 
  RefreshCw, 
  Truck,
  ArrowDown,
  ArrowUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Zap,
  Camera,
  Image as ImageIcon,
  X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import api from "../services/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Monitor() {
  const [weighings, setWeighings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showOnlyNew, setShowOnlyNew] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  const fetchWeighings = async () => {
    try {
      const res = await api.get("/weighings", { params: { limit: 50, only_new: showOnlyNew } });
      setWeighings(res.data);
    } catch (error) {
      console.error("Error fetching weighings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeighings();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchWeighings, 3000); // Refresh every 3s
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, showOnlyNew]);

  const handleRefresh = () => {
    setLoading(true);
    fetchWeighings();
    toast.success("Pesagens atualizadas");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("pt-BR");
  };

  const getTipoAcesso = (tipo) => {
    switch (tipo) {
      case 2: return { label: "Entrada", icon: ArrowDown, color: "text-green-400" };
      case 4: return { label: "Saída", icon: ArrowUp, color: "text-blue-400" };
      case 8: return { label: "Carga/Descarga", icon: Truck, color: "text-yellow-400" };
      case 32: return { label: "Passagem Direta", icon: Zap, color: "text-cyan-400" };
      default: return { label: "Outro", icon: Scale, color: "text-zinc-400" };
    }
  };

  const newWeighingsCount = weighings.filter(w => w.is_new).length;
  const totalWeight = weighings.reduce((acc, w) => acc + Math.round(w.peso_bruto || 0), 0);

  return (
    <div className="space-y-6 animate-slide-in" data-testid="monitor-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono">Monitor de Pesagens</h1>
          <p className="text-sm text-zinc-500">Acompanhamento em tempo real das pesagens da balança</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch 
              id="only-new" 
              checked={showOnlyNew} 
              onCheckedChange={setShowOnlyNew}
              data-testid="show-only-new-switch"
            />
            <Label htmlFor="only-new" className="text-sm text-zinc-400">Apenas novas</Label>
          </div>
          <Button 
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30" : ""}
            data-testid="auto-refresh-monitor-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            Auto
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefresh}
            data-testid="refresh-monitor-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="stat-total-weighings">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500">Total de Pesagens</p>
                <p className="text-3xl font-bold text-white font-mono">{weighings.length}</p>
              </div>
              <Scale className="w-10 h-10 text-cyan-400/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="stat-new-weighings">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500">Novas Pesagens</p>
                <p className="text-3xl font-bold text-cyan-400 font-mono">{newWeighingsCount}</p>
              </div>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${newWeighingsCount > 0 ? 'bg-cyan-400/20 animate-pulse-glow' : 'bg-zinc-800'}`}>
                <Zap className={`w-5 h-5 ${newWeighingsCount > 0 ? 'text-cyan-400' : 'text-zinc-600'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="stat-total-weight">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500">Peso Total</p>
                <p className="text-3xl font-bold text-white font-mono">{totalWeight}<span className="text-lg text-zinc-500">kg</span></p>
              </div>
              <Truck className="w-10 h-10 text-green-400/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weighings List */}
      <Card className="bg-zinc-900/50 border-zinc-800" data-testid="weighings-list-card">
        <CardHeader>
          <CardTitle className="text-lg font-mono text-white flex items-center gap-2">
            <Scale className="w-5 h-5 text-cyan-400" />
            Registro de Pesagens
            {newWeighingsCount > 0 && (
              <Badge className="bg-cyan-500/20 text-cyan-400 ml-2">
                {newWeighingsCount} novas
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-cyan-400 animate-spin" />
            </div>
          ) : weighings.length > 0 ? (
            <div className="space-y-2">
              {weighings.map((weighing) => {
                const tipoAcesso = getTipoAcesso(weighing.tipo_acesso);
                const TipoIcon = tipoAcesso.icon;
                
                return (
                  <div 
                    key={weighing.id}
                    className={`flex items-center gap-4 p-4 rounded-sm bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors ${weighing.is_new ? 'animate-flash-new border-l-2 border-cyan-400' : ''}`}
                    data-testid={`weighing-${weighing.codigo_movimento}`}
                  >
                    {/* Type Icon */}
                    <div className={`p-2 rounded-sm bg-zinc-800 ${tipoAcesso.color}`}>
                      <TipoIcon className="w-5 h-5" />
                    </div>

                    {/* Main Info */}
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <p className="text-xs text-zinc-500">Placa</p>
                        <p className="text-lg font-mono font-bold text-white">{weighing.placa || "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500">Peso Bruto</p>
                        <p className="text-lg font-mono font-bold text-cyan-400">{Math.round(weighing.peso_bruto || 0)} kg</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500">Tipo</p>
                        <Badge className={`${tipoAcesso.color} bg-transparent border border-current`}>
                          {tipoAcesso.label}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500">Código</p>
                        <p className="text-sm font-mono text-zinc-300">{weighing.codigo_movimento}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500">Data/Hora</p>
                        <div className="flex items-center gap-1 text-sm text-zinc-400">
                          <Clock className="w-3 h-3" />
                          {formatDate(weighing.data_movimento)}
                        </div>
                      </div>
                    </div>

                    {/* Images */}
                    {weighing.imagens && weighing.imagens.length > 0 && (
                      <div className="flex items-center gap-1">
                        {weighing.imagens.map((img, idx) => (
                          <Button
                            key={idx}
                            variant="outline"
                            size="sm"
                            className="p-1 h-8 w-8"
                            onClick={() => {
                              setSelectedImage({
                                url: `${BACKEND_URL}/api/images/${weighing.codigo_erp || `GW-${weighing.codigo_movimento}`}/${img.tipo}`,
                                tipo: img.tipo,
                                placa: weighing.placa
                              });
                              setImageDialogOpen(true);
                            }}
                            title={`Ver ${img.tipo}`}
                          >
                            <Camera className="w-4 h-4 text-cyan-400" />
                          </Button>
                        ))}
                      </div>
                    )}

                    {/* Status */}
                    <div className="flex items-center gap-2">
                      {weighing.is_new && (
                        <Badge className="bg-cyan-500/20 text-cyan-400">NOVA</Badge>
                      )}
                      {weighing.integrado ? (
                        <div className="flex items-center gap-1 text-green-400">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-xs">Integrado</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-yellow-400">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-xs">Pendente</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
              <Scale className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhuma pesagem registrada</p>
              <p className="text-sm">As pesagens aparecerão aqui quando o RODOPESO enviar dados</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-cyan-500/5 border-cyan-500/20">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-cyan-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-cyan-400">Detecção Inteligente de Novas Pesagens</p>
              <p className="text-xs text-zinc-400 mt-1">
                O gateway monitora as últimas 20 pesagens enviadas pelo RODOPESO a cada 5 segundos.
                Quando uma nova pesagem é detectada (alteração na lista), ela é automaticamente
                encaminhada para o ERP SENIOR via SOAP.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Image Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-mono text-white flex items-center gap-2">
              <Camera className="w-5 h-5 text-cyan-400" />
              {selectedImage?.tipo} - Placa: {selectedImage?.placa}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-zinc-800 rounded-sm min-h-[300px]">
            {selectedImage && (
              <img 
                src={selectedImage.url} 
                alt={`Imagem ${selectedImage.tipo}`}
                className="max-w-full max-h-[500px] object-contain rounded"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div class="text-zinc-500 text-center"><p>Imagem não disponível</p></div>';
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
